import React from 'react';

/**
 * Guide tab: plain-language documentation of every admin control, written for
 * the person running a tenant (not an engineer). Kept in-app rather than in a
 * PDF so it can't drift from the UI it describes — when a control changes,
 * change its paragraph here in the same commit.
 */

const fontStack = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

const P = ({ children, dim }) => (
  <p style={{ fontSize: '0.88rem', lineHeight: 1.6, color: dim ? '#666' : '#333', margin: '0 0 10px' }}>{children}</p>
);
const UL = ({ children }) => (
  <ul style={{ fontSize: '0.88rem', lineHeight: 1.6, color: '#333', margin: '0 0 12px', paddingLeft: 20 }}>{children}</ul>
);
const LI = ({ children }) => <li style={{ margin: '0 0 5px' }}>{children}</li>;
const K = ({ children }) => (
  <strong style={{ fontWeight: 600, color: '#111' }}>{children}</strong>
);
const Note = ({ children }) => (
  <p style={{ fontSize: '0.82rem', lineHeight: 1.55, color: '#555', background: '#f8f9fa',
              borderLeft: '3px solid #d8dce0', borderRadius: '0 6px 6px 0',
              padding: '9px 12px', margin: '0 0 12px' }}>{children}</p>
);

// A chevron is the only thing telling a reader these rows open, so it can't be
// inline style: ::marker and [open] both need a stylesheet.
const SECTION_CSS = `
.guide-section > summary { list-style: none; cursor: pointer; }
.guide-section > summary::-webkit-details-marker { display: none; }
.guide-section > summary::before {
  content: ''; flex: none; width: 6px; height: 6px; margin-right: 2px;
  border-right: 1.5px solid #999; border-bottom: 1.5px solid #999;
  transform: rotate(-45deg); transition: transform .15s ease;
}
.guide-section[open] > summary::before { transform: rotate(45deg); }
`;

function Section({ title, summary, children, open = false }) {
  return (
    <details className="guide-section" open={open} style={{ borderTop: '1px solid #eee', padding: '14px 0' }}>
      <summary style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: '1rem', fontWeight: 600, color: '#111' }}>{title}</span>
        <span style={{ fontSize: '0.82rem', color: '#888' }}>{summary}</span>
      </summary>
      <div style={{ paddingTop: 12, paddingLeft: 16, maxWidth: 680 }}>{children}</div>
    </details>
  );
}

const OUTCOME_HELP = [
  ['Answered', 'The assistant found the answer in your published materials and gave it.'],
  ['Not found', 'The assistant could not find the answer in your published materials, so it said so. Either nothing was close enough to the question, or the pages it found cover the topic but not that detail. This is the list you can fix by adding or rewording content.'],
  ['Declined', 'Declined on purpose: the question asked for personal advice ("which option is best for me"), included personal details such as a Social Security number (masked before anything is stored), or named an organization with a name close to yours.'],
  ['Greetings', 'A hello or a thank-you, answered with a greeting.'],
  ['Other', 'Shown only when it happens: the daily limit for this site was reached, or something failed mid-answer. Tell us if you see more than the occasional one.'],
];

export default function GuideTab() {
  return (
    <div style={{ fontFamily: fontStack, maxWidth: 760 }}>
      <style>{SECTION_CSS}</style>
      <h2 style={{ fontSize: '1.35rem', fontWeight: 600, margin: '0 0 6px' }}>How to use this admin</h2>
      <P dim>
        Four tabs. <K>Training</K> adds content. <K>Sources</K> shows what the assistant knows and who can see it.
        <K> Widget &amp; Persona</K> controls how it looks and sounds on your site. <K>Analytics</K> shows what people asked.
        Open any section below for the details.
      </P>

      <Section title="Start here" summary="the first-run order" open>
        <P>If this is a new site, do it in this order:</P>
        <UL>
          <LI><K>Add your content</K> on the Training tab, then wait for it to finish.</LI>
          <LI><K>Check it landed</K> on the Sources tab. Every page, PDF and video the assistant can use is listed there.</LI>
          <LI><K>Set the look and voice</K> on the Widget &amp; Persona tab. The preview on the right is live, so you can ask it real questions while you edit.</LI>
          <LI><K>Copy the embed snippet</K> at the bottom of that tab and give it to whoever manages your website.</LI>
          <LI><K>Come back to Analytics</K> after a week to see what people asked and what the assistant could not answer.</LI>
        </UL>
        <Note>
          You signed in with your site address and a customer key. The key is what identifies your account to
          every screen here. If a tab says it cannot load, that is almost always the key — sign out and back in.
        </Note>
      </Section>

      <Section title="Training" summary="add videos to the assistant">
        <P>
          Two ways to add material. Both write into the same library you see on the Sources tab.
        </P>
        <P>
          <K>Train on Channel</K> pulls in every video on the channel shown in the first box. That box is fixed to the
          site you signed in as. Use this once at the start, and again after you publish a batch of videos.
        </P>
        <P>
          <K>Add Video</K> takes a single video URL. Use it for one-off additions. You can attach PDFs at the same time:
          pick them under <K>Attach PDFs</K> and they are added alongside the video.
        </P>
        <P>
          <K>Force reprocess</K> re-reads material the assistant already has. Leave it off. Turn it on only when a video
          was processed badly, or when you have replaced a PDF with a corrected version. It costs time and money on
          content that is already fine.
        </P>
        <P>
          Once training starts, a progress panel appears below the forms and updates every few seconds. You can leave the
          page; the work continues on our servers. Come back and reload to check on it.
        </P>
        <Note>
          Website pages are not added here. Crawling a website is something we run for you. Email us the pages or the
          sitemap and we will pull them in.
        </Note>
      </Section>

      <Section title="Sources" summary="what the assistant knows, and who it is for">
        <P>
          One row per document. The title links to the original. <K>Type</K> is Page, PDF or Video. <K>Linked from</K>
          {' '}shows which of your crawled pages pointed at that file, which is how you find where a stray PDF came from.
          {' '}<K>Indexed</K> is the date we last read it.
        </P>
        <P>
          The search box and the three dropdowns narrow the list. The counts in the dropdowns are for the whole library,
          the number on the right is how many rows you are looking at now.
        </P>

        <h4 style={{ fontSize: '0.9rem', margin: '16px 0 6px' }}>Turning a source off</h4>
        <P>
          The <K>Disable</K> button on the right takes a document out of answers. It takes about 30 seconds to take
          effect. Future crawls skip a disabled document, so it will not quietly come back. Use this for an outdated
          policy page or a form that has been replaced.
        </P>
        <P>
          <K>Enable</K> puts it back from what we have stored. If a document has been gone long enough that the stored
          copy is no longer there, you will see a message asking for a fresh crawl. Email us and we will re-run it.
        </P>

        <h4 style={{ fontSize: '0.9rem', margin: '16px 0 6px' }}>Audiences</h4>
        <P>
          Audiences let one assistant serve groups that should see different material, for example active members and
          retirees. Set them up in the panel at the top of this tab.
        </P>
        <UL>
          <LI><K>Label</K> is what visitors see when they are asked who they are. Keep it short and obvious.</LI>
          <LI><K>Description</K> is for you, not for visitors.</LI>
          <LI><K>Starter questions</K> are the suggested questions this group sees instead of the site-wide ones. Up to four. Leave empty to use the site-wide ones.</LI>
        </UL>
        <P>
          You can have up to eight audiences. You cannot delete one while documents are still tagged with it: filter the
          list by that audience, select the rows, and bulk-remove the tag first. You also cannot delete the last one. To
          stop asking visitors who they are, turn the gate off on the Widget tab instead.
        </P>
        <P>
          Tag a document using the <K>Audience</K> button in its row. A document with no tags says <K>Everyone</K> and is
          available to all visitors. That is the right answer for most content. Tag only the material that genuinely
          should not reach the other group.
        </P>
        <P>
          To tag in bulk, tick the boxes on the left, then use the blue bar that appears. <K>Set</K> replaces whatever
          tags those documents had. <K>Add</K> and <K>Remove</K> adjust them without touching the rest. Setting with
          nothing ticked clears the tags, which makes those documents available to everyone.
        </P>
        <Note>
          Audience changes reach search in 30 to 60 seconds. Audiences belong to the content library, so every site using
          the same library shares them.
        </Note>
      </Section>

      <Section title="Widget & Persona" summary="how it looks and sounds on your site">
        <P>
          Pick the site at the top if you have more than one. Everything you change here is a draft until you press
          {' '}<K>Save</K>. Saving pushes the change to your live site within a minute. Nothing here affects the answers
          themselves, only their presentation and wording.
        </P>
        <P>
          The panel on the right is the real assistant running against your real content. Ask it something. What you see
          is what visitors get.
        </P>

        <h4 style={{ fontSize: '0.9rem', margin: '16px 0 6px' }}>Branding</h4>
        <UL>
          <LI><K>Bot name</K> and <K>Header subtitle</K> are the two lines at the top of the panel.</LI>
          <LI><K>Accent color</K> colors the header, the launcher button and the visitor's messages. If you pick a pale color we warn you, because white text on it is hard to read.</LI>
          <LI><K>Launcher icon</K> and <K>Position</K> control the floating button on your site.</LI>
          <LI><K>Sources under each answer</K>: collapsed keeps answers tidy, expanded shows the links every time. Collapsed suits most sites.</LI>
          <LI><K>Panel width and height</K> only affect desktop. On phones the panel fills the screen regardless.</LI>
        </UL>

        <h4 style={{ fontSize: '0.9rem', margin: '16px 0 6px' }}>Content</h4>
        <UL>
          <LI><K>Ask visitors who they are</K> turns on the audience question before the first message. It stays greyed out until you have defined audiences on the Sources tab.</LI>
          <LI><K>Audience prompt</K> is the wording of that question.</LI>
          <LI><K>Starter questions</K> are the suggestions on the empty panel. Up to four. Pick real questions people ask, not categories. An audience with its own starter questions overrides these.</LI>
          <LI><K>Input placeholder</K> is the grey hint in the message box.</LI>
          <LI><K>Per-answer disclaimer</K> appears under every answer. Keep it to one line; it is repeated constantly.</LI>
          <LI><K>Terms link</K> adds a link in the footer of the panel.</LI>
        </UL>

        <h4 style={{ fontSize: '0.9rem', margin: '16px 0 6px' }}>Persona</h4>
        <P>
          <K>Communication style</K> changes the tone, not the facts. The line under the dropdown tells you what each one
          does. Professional is the default and suits most public-facing sites.
        </P>
        <P>
          <K>Custom "I don't know" message</K> is what visitors see when the assistant cannot answer. This is worth
          writing yourself, because it is the moment a visitor needs somewhere else to go. Point them at a phone number
          or a contact page. Leave it empty for our default. Write <K>{'{agency}'}</K> or <K>{'{contact}'}</K> and we
          fill in your name and contact details. There is a separate box for the Spanish version.
        </P>
        <P>
          <K>End answered questions with a follow-up line</K> adds a closing line such as "Anything else I can help
          with?" to answers. Off by default.
        </P>

        <h4 style={{ fontSize: '0.9rem', margin: '16px 0 6px' }}>Embed snippet</h4>
        <P>
          The block at the bottom is the code that puts the assistant on your site. Press <K>Copy snippet</K> and send it
          to whoever edits your website; it goes just before the closing <K>&lt;/body&gt;</K> tag on every page that
          should have the assistant. You only do this once. Later changes on this tab reach the site on their own, with
          no new snippet.
        </P>
        <Note>
          Your site's address has to be on our approved list before the assistant will load there. If the snippet is in
          place and nothing appears, that is usually why. Send us the address.
        </Note>
      </Section>

      <Section title="Analytics" summary="what people asked, and what you are missing">
        <P>
          Pick a period at the top: 7, 14 or 30 days. The cards underneath are the headline: how many questions came
          in, and what happened to them. Click a card to open the list of those questions, with the answer each one got.
        </P>
        <P>
          Not every unanswered question is a failure. Some are the assistant correctly declining to give personal
          advice. The cards tell you which kind you are getting:
        </P>
        <table style={{ borderCollapse: 'collapse', fontSize: '0.82rem', margin: '0 0 14px', width: '100%' }}>
          <tbody>
            {OUTCOME_HELP.map(([outcome, meaning]) => (
              <tr key={outcome} style={{ borderTop: '1px solid #f0f0f0', verticalAlign: 'top' }}>
                <td style={{ padding: '7px 12px 7px 0', whiteSpace: 'nowrap', fontWeight: 600, color: '#111' }}>{outcome}</td>
                <td style={{ padding: '7px 0', color: '#444', lineHeight: 1.5 }}>{meaning}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <P>
          <K>Language</K> appears once people write in more than one language. If a second language shows up in numbers,
          that is your cue to write the Spanish "I don't know" message and check your content covers those readers.
        </P>
        <P>
          <K>Audience</K> appears when you have set up audiences, with how many of each group's questions went
          unanswered. One group going unanswered far more than the others usually means its content is under-tagged:
          material it needs is tagged to a different audience, so it never sees it.
        </P>
        <P>
          The question lists show personal details masked, because they are masked before anything is stored, so you
          will see questions with pieces blanked out.
        </P>
        <P>
          <K>Not found</K> is the list worth acting on: real questions your assistant could not answer from your
          materials. Read it every couple of weeks. Repeated questions on the same theme mean either the page exists
          and needs better wording, or it does not exist and should.
        </P>
      </Section>

      <Section title="Fixing common problems" summary="before you email us">
        <UL>
          <LI><K>The assistant says "I don't have that" too often.</K> Open the Not found list. Questions with no page behind them mean the content is not there, so add it. Questions whose page does exist mean the page covers the topic but buries the detail.</LI>
          <LI><K>It quotes something out of date.</K> Find it on Sources and press Disable. It stops being used within about 30 seconds.</LI>
          <LI><K>One group of visitors goes unanswered constantly.</K> Their audience is over-tagged. Filter Sources by that audience and check how little it can see; general material should stay on Everyone.</LI>
          <LI><K>A change on the Widget tab did not show up.</K> Press Save, then wait a minute and reload your site. If the preview here looks right and your site does not, it is caching on your side.</LI>
          <LI><K>A tab will not load.</K> Sign out and sign back in. Your key is what those screens use to identify you.</LI>
        </UL>
        <P>
          Things only we can do: crawling or re-crawling a website, adding a new site to your account, approving a new
          web address to embed on, and raising the daily spending cap. Email us and we will take care of it.
        </P>
      </Section>
    </div>
  );
}
